import { config } from '@abyss/web/tools/config';
import { t } from 'i18next';
/* istanbul ignore file */
import mapboxgl from 'mapbox-gl';
import { Dispatch, SetStateAction } from 'react';

import {
  CLUSTER_RADIUS_DEFAULT,
  CLUSTER_ZOOM_THRESHOLD_DEFAULT,
  Constants,
  GROUP_DISTANCE_THRESHOLD_MILES_DEFAULT,
  Navigation,
  PAGE_SIZE_MAP_DEFAULT,
  PAGE_SIZE_MAP_LARGE,
  ProviderType,
} from '../common/Constants';
import { defaultStringToEmpty } from '../common/PSXHeader/SmartChoice/utils';
import { handleLinkAndModalTrack } from '../common/Utils/adobeTrackUtils';
import { getLanguage } from '../frontends/ProviderSearch/context/Internationalization/helpers';
import { FacilityLocationsResponse } from '../models/FacilityDetails';
import { Provider } from '../models/Provider';
import { ProviderLocation } from '../models/ProviderDetails';
import { Directions } from '../models/RouteDirections';
import { getDistance } from '../utils/card.utils';
import {
  CLUSTER_LAYER,
  RESULT_LAYER,
  ROUTE_LAYER,
  USER_LAYER,
} from './generalMap.utils';

export const USER_LOCATION_PNG = `${config(
  'CDN_BASE_URL'
)}/cdn/assets/images/user_location.png`;
export const GEHA_USER_LOCATION_PNG = `${config(
  'CDN_BASE_URL'
)}/cdn/assets/images/geha_user_location_v2.png`;
export const RESULT_LOCATION_PNG = `${config(
  'CDN_BASE_URL'
)}/cdn/assets/images/location_pin.png`;
export const MAP_GROUP_ICON = `${config(
  'CDN_BASE_URL'
)}/cdn/assets/images/map_group_icon.png`;

type MapMarker = {
  id: string;
  marker: any;
};

export interface Feature {
  type: string;
  id: string | undefined;
  properties: {
    description: string;
    providerId: string | undefined;
    type: string;
  };
  geometry: {
    type: string;
    coordinates: (string | undefined)[];
  };
}

let clickResultId = null;
let hoverResultId = null;
let curNavType = 'driving-traffic';
let mapMarkers: MapMarker[] = [];

export const loadUserLocationPin = (
  map,
  userLng: string,
  userLat: string,
  isGeha: boolean
) => {
  const userLayer = map.getSource(USER_LAYER);
  if (userLayer) {
    map.jumpTo({ center: [userLng, userLat] });
    userLayer.setData({
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [userLng, userLat],
          },
        },
      ],
    });
    return;
  }

  const userLocationPng = isGeha ? GEHA_USER_LOCATION_PNG : USER_LOCATION_PNG;
  map.loadImage(userLocationPng, (error, image) => {
    if (error) {
      throw error;
    }

    map.addImage('userLocation', image);
  });

  map?.addSource(USER_LAYER, {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [userLng, userLat],
          },
        },
      ],
    },
  });

  map?.addLayer({
    id: 'curLocationLayer',
    type: 'symbol',
    source: USER_LAYER,
    layout: {
      'icon-image': 'userLocation',
      'icon-allow-overlap': true,
    },
  });
};

export const setZoomLevel = async (map, coordinates?): Promise<void> => {
  let coordsAr = coordinates;
  if (!coordsAr) {
    coordsAr = mapMarkers?.map((marker) => {
      const markerCoords = marker.marker.getLngLat();

      return [markerCoords.lng, markerCoords.lat];
    });
  }

  if (coordsAr?.length) {
    const bounds = new mapboxgl.LngLatBounds();
    if (bounds.extend != undefined) {
      coordsAr?.forEach((f: [number, number]) => bounds.extend(f));
      await map?.fitBounds(bounds, { maxZoom: 20, padding: 10 });
    }
  }
};

const clickMapPinPoint = (
  el,
  map,
  location,
  practitioner,
  highlightPinCallback,
  updatePin,
  enableMapEnhancements,
  mobileScreen
) => {
  clickResultId = location.id;

  highlightPinCallback({
    providerId: practitioner.providerId ?? practitioner.locationId,
    from: 'mapView',
  });
  updatePin?.(practitioner);
  if (mobileScreen) {
    updateIconSize(true, el, enableMapEnhancements);
  }

  setResultIcon(map, hoverResultId, false, highlightPinCallback, null);
  setResultIcon(
    map,
    clickResultId,
    true,
    highlightPinCallback,
    practitioner.providerId ?? practitioner.locationId
  );
  handleLinkAndModalTrack(
    'click map pin point',
    'body: results page map view',
    'map view pin point'
  );
};

const hoverMapPinPoint = (
  el,
  map,
  location,
  practitioner,
  highlightPinCallback,
  updatePin,
  enableMapEnhancements
) => {
  if (clickResultId === null) {
    if (hoverResultId !== null) {
      setResultIcon(map, hoverResultId, false, highlightPinCallback, null);
    }
    updateIconSize(true, el, enableMapEnhancements);
    hoverResultId = location.id;
    setResultIcon(
      map,
      hoverResultId,
      true,
      highlightPinCallback,
      practitioner.providerId ?? practitioner.locationId
    );
    updatePin?.(practitioner);
  }
};

export const closePopUp = (el, popUp, tooltip, updatePin) => {
  (popUp as HTMLElement).style.visibility = 'hidden';
  (tooltip as HTMLElement).style.visibility = 'hidden';
  el.focus();
  updatePin?.(null);
};

export const groupedPinElement = (
  count,
  map,
  setGroupedLocationDrawerOpen: (open: boolean) => void,
  setDrawerContent: (content: any) => void,
  coords,
  providerIds,
  locationIds
) => {
  let drawerContent = {};
  const el = document.createElement('button'); // Accessibility: focus needed for keyboard navigation
  el.className = 'grouped-pin';
  el.setAttribute('data-testid', 'grouped-location-pin');
  el.style.display = 'flex';
  el.style.alignItems = 'center';
  el.style.justifyContent = 'center';
  el.style.maxWidth = '72px';
  el.style.paddingTop = '5px';
  el.style.paddingBottom = '5px';
  el.style.paddingLeft = '12px';
  el.style.paddingRight = '12px';
  el.style.height = '34px';
  el.style.borderRadius = '8px';
  el.style.backgroundColor = 'var(--abyss-psx-parcel-colors-primary1)';
  el.style.border = '2px solid white';

  const arrow = document.createElement('div');
  arrow.className = 'group-arrow';
  arrow.style.border = '2px solid white';
  arrow.style.borderLeft = '2px solid transparent';
  arrow.style.borderTop = '2px solid transparent';
  arrow.style.width = '15px';
  arrow.style.height = '15px';
  arrow.style.backgroundColor = 'var(--abyss-psx-parcel-colors-primary1)';
  arrow.style.position = 'absolute';
  arrow.style.top = '100%';
  arrow.style.left = '50%';
  arrow.style.transform = 'translate(-50%, -50%) rotate(45deg)';
  arrow.style.zIndex = '-1';
  el.appendChild(arrow);

  const icon = document.createElement('img');
  icon.src = MAP_GROUP_ICON;
  icon.alt = '';
  icon.style.width = '16px';
  icon.style.height = '16px';
  icon.style.marginRight = '8px';
  el.appendChild(icon);

  const countEl = document.createElement('span');
  countEl.className = 'group-pin-count';
  countEl.textContent = String(count) || '0';
  countEl.style.color = 'white';
  countEl.style.fontSize = '16px';
  countEl.style.fontWeight = '700';
  el.appendChild(countEl);

  el.addEventListener('mouseenter', () => {
    el.style.backgroundColor = 'var(--abyss-psx-parcel-colors-info1)';
    el.style.cursor = 'pointer';
    el.style.zIndex = '1';
    arrow.style.backgroundColor = 'var(--abyss-psx-parcel-colors-info1)';
  });
  el.addEventListener('mouseleave', () => {
    el.style.backgroundColor = 'var(--abyss-psx-parcel-colors-primary1)';
    el.style.zIndex = '0';
    arrow.style.backgroundColor = 'var(--abyss-psx-parcel-colors-primary1)';
  });

  el.addEventListener('click', (event) => {
    el.focus();
    event?.stopPropagation();
    if (typeof setGroupedLocationDrawerOpen === 'function') {
      setGroupedLocationDrawerOpen(true);
    }
    drawerContent = {
      title: coords.join(', '),
      numberProviders: count,
      ...drawerContent,
      providerIds,
      locationId: locationIds,
    };
    if (typeof setDrawerContent === 'function') {
      setDrawerContent(drawerContent);
    }
  });

  el.addEventListener('focus', (e) => {
    e.preventDefault();
    const bounds = map.getBounds();
    if (!bounds.contains(coords))
      map.easeTo({
        center: coords,
      });
  });

  return el;
};

const createLocationMapPin = (
  markerId,
  practitioner,
  location,
  map,
  highlightPinCallback,
  updatePin,
  enableMapEnhancements,
  mobileScreen,
  disabledPinAction,
  enableMapViewLargeResults
) => {
  const el = document.createElement('button');
  el.setAttribute('data-testid', 'location-map-pin');
  if (enableMapViewLargeResults) {
    el.className = 'dotMarker';
    el.style.width = '16px';
    el.style.height = '16px';
    el.style.borderRadius = '50%';
    el.style.backgroundColor = 'var(--abyss-psx-parcel-colors-primary1)';
    el.style.border = '2px solid white';
  } else {
    const elImage = document.createElement('img');
    elImage.src = RESULT_LOCATION_PNG;
    elImage.className = 'map-icon';
    elImage.alt = '';
    el.appendChild(elImage);
  }

  el.className = 'custom-marker';
  el.id = `custom-marker-${markerId}`;
  el.ariaLabel = `${t('ACCESSIBILITY_LABELS.MAP_PINPOINT')}, ${
    practitioner.providerName ??
    (practitioner?.providerGroupsAffiliations
      ? practitioner?.providerGroupsAffiliations?.[0]?.value
      : '')
  } ${getDistance(practitioner.distance)} ${t('DATA_CARD.MILES')}`;
  el.tabIndex = 0;

  let enterPin = false;
  if (!disabledPinAction) {
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === 'Tab' || e.key === 'Escape') {
        if (e.key === 'Enter') {
          clickMapPinPoint(
            el,
            map,
            location,
            practitioner,
            highlightPinCallback,
            updatePin,
            enableMapEnhancements,
            mobileScreen
          );
          enterPin = true;
        }
        const rootElem = document.querySelector('.datacard-popup');
        const tooltip = document.querySelector('.mapboxgl-popup-tip');

        if (e.key === 'Tab' && enterPin) {
          (rootElem as HTMLElement)?.focus();
          enterPin = false;
        }
        if (e.key === 'Escape') {
          closePopUp(el, rootElem, tooltip, updatePin);
          clickResultId = null;
        }
      }
    });

    el.addEventListener('mouseenter', () => {
      if (!enableMapEnhancements || !mobileScreen) {
        hoverMapPinPoint(
          el,
          map,
          location,
          practitioner,
          highlightPinCallback,
          updatePin,
          enableMapEnhancements
        );
      }
    });

    el.addEventListener('click', (event) => {
      if (clickResultId !== null && mobileScreen && enableMapEnhancements) {
        updateIconSize(false, selectPin(clickResultId), enableMapEnhancements);
      }
      el.focus();
      enterPin = true;
      event?.stopPropagation();

      clickMapPinPoint(
        el,
        map,
        location,
        practitioner,
        highlightPinCallback,
        updatePin,
        enableMapEnhancements,
        mobileScreen
      );
    });

    el.addEventListener('mouseleave', () => {
      if (!enableMapEnhancements || !mobileScreen) {
        updateIconSize(false, el, enableMapEnhancements);
        if (clickResultId === null) {
          updatePin?.(null);
          highlightPinCallback({ providerId: '', from: 'mapView' });
        }
      }
    });

    el.addEventListener('focus', () => {
      clickResultId = null;
    });
  }
  return el;
};

export const loadResultLocationPins = async (
  mobileScreen,
  map,
  features,
  coordinates,
  highlightPinCallback,
  disabledPinAction,
  updatePin,
  enableMapEnhancements: boolean,
  enableMapViewLargeResults: boolean
) => {
  await map?.isStyleLoaded();

  if (!map?.getSource(RESULT_LAYER)) {
    map?.addSource(RESULT_LAYER, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features,
      },
    });

    map?.addLayer({
      id: RESULT_LAYER,
      type: 'symbol',
      source: RESULT_LAYER,
    });
  }

  const updateMarkers = (features: any = []) => {
    features?.forEach((location, index) => {
      const { description } = location.properties;
      const practitioner = JSON.parse(description);
      const markerId = practitioner.providerId ?? practitioner.locationId;

      if (mapMarkers.find((marker) => marker.id === markerId)) {
        return;
      }

      const el = createLocationMapPin(
        markerId,
        practitioner,
        location,
        map,
        highlightPinCallback,
        updatePin,
        enableMapEnhancements,
        mobileScreen,
        disabledPinAction,
        enableMapViewLargeResults
      );

      const newMarker = new mapboxgl.Marker(el)
        .setLngLat(coordinates[index])
        ?.addTo(map);

      mapMarkers.push({
        id: markerId,
        marker: newMarker,
      });
    });
  };

  updateMarkers(features);
};

export const clusterPinElement = (count, map, coordinates, clusterId) => {
  const el = document.createElement('button'); // Accessibility: focus needed for keyboard navigation
  el.className = 'cluster-pin';
  el.setAttribute('data-testid', 'cluster-pin');
  el.style.display = 'flex';
  el.style.alignItems = 'center';
  el.style.justifyContent = 'center';
  el.style.paddingTop = '4px';
  el.style.paddingBottom = '4px';
  el.style.paddingLeft = '12px';
  el.style.paddingRight = '12px';
  el.style.height = '34px';
  el.style.borderRadius = '25px';
  el.style.backgroundColor = 'var(--abyss-psx-parcel-colors-interactive2)';
  el.style.border = '2px solid white';

  const countEl = document.createElement('span');
  countEl.className = 'cluster-pin-count';
  countEl.textContent = `${count} ${t('results')}`;
  countEl.style.color = 'white';
  countEl.style.fontSize = '16px';
  countEl.style.fontWeight = 'bold';
  el.appendChild(countEl);

  el.addEventListener('mouseenter', () => {
    el.style.backgroundColor = 'var(--abyss-psx-parcel-colors-interactive1)';
    el.style.cursor = 'pointer';
    el.style.zIndex = '1';
  });
  el.addEventListener('mouseleave', () => {
    el.style.backgroundColor = 'var(--abyss-psx-parcel-colors-interactive2)';
    el.style.zIndex = '0';
  });
  el.onclick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    map
      .getSource(CLUSTER_LAYER)
      .getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;

        map.easeTo({
          center: coordinates,
          zoom,
        });
      });
  };

  el.addEventListener('focus', (e) => {
    e.preventDefault();
    const bounds = map.getBounds();
    if (!bounds.contains(coordinates))
      map.easeTo({
        center: coordinates,
      });
  });

  return el;
};

const getClusterCountAndFeatures = (
  clusterSource,
  clusterId
): Promise<[number, Feature[]]> =>
  new Promise((resolve) => {
    const offset = 0;
    const maxClusterLeaves = 1000;
    const validPropertyTypes = ['provider', 'location'];
    clusterSource.getClusterLeaves(
      clusterId,
      maxClusterLeaves,
      offset,
      (_, features) => {
        const totalCount =
          features?.reduce((acc, feature) => {
            if (validPropertyTypes.includes(feature.properties?.type)) {
              return acc + 1;
            } else {
              acc += feature.properties?.length || 0;
            }
            return acc;
          }, 0) || 0;
        resolve([totalCount, features]);
      }
    );
  });

const createGroupedPinMarker = (
  feature,
  setGroupedLocationDrawerOpen,
  setDrawerContent,
  map,
  mapMarkers
) => {
  const locationIds = feature.properties.map(
    (property: { properties?: { description?: string } }) => {
      const description = property?.properties?.description;
      return description ? JSON.parse(description)?.locationId : null;
    }
  );
  const coords =
    feature?._geometry?.coordinates || feature?.geometry?.coordinates;
  const markerId = `${coords[0]},${coords[1]}`;
  if (mapMarkers.find((marker) => marker.id === markerId)) {
    return;
  }
  const props = feature.properties;
  const providerIds = Object.values(props).map(
    (property: { properties?: { providerId?: string } }) =>
      property?.properties?.providerId
  );
  const count = Object.keys(props).length;
  const el = groupedPinElement(
    count,
    map,
    setGroupedLocationDrawerOpen,
    setDrawerContent,
    coords,
    providerIds,
    locationIds
  );

  const newMarker = new mapboxgl.Marker({
    element: el,
  }).setLngLat(coords);
  mapMarkers.push({
    id: markerId,
    marker: newMarker,
  });
  newMarker.addTo(map);
};

export const loadClusteredLocationPins = async (
  map,
  featuresAr,
  highlightPinCallback,
  updatePin,
  enableMapEnhancements,
  mobileScreen,
  disabledPinAction,
  enableMapViewLargeResults,
  clusterMaxZoom,
  clusterRadius,
  groupDistanceThreshold,
  setGroupedLocationDrawerOpen,
  setDrawerContent
) => {
  await map?.isStyleLoaded();
  const groupDistanceThresholdValue = stringToFloatOrDefault(
    groupDistanceThreshold,
    GROUP_DISTANCE_THRESHOLD_MILES_DEFAULT
  );
  const clusterMaxZoomValue = stringToIntegerOrDefault(
    clusterMaxZoom,
    CLUSTER_ZOOM_THRESHOLD_DEFAULT
  );
  const clusterRadiusValue = stringToIntegerOrDefault(
    clusterRadius,
    CLUSTER_RADIUS_DEFAULT
  );

  if (!map?.getSource(CLUSTER_LAYER)) {
    map?.addSource(CLUSTER_LAYER, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: featuresAr,
      },
      cluster: true,
      clusterMaxZoom: clusterMaxZoomValue,
      clusterRadius: clusterRadiusValue,
    });

    map?.addLayer({
      id: CLUSTER_LAYER,
      type: 'symbol',
      source: CLUSTER_LAYER,
    });
  } else {
    map.getSource(CLUSTER_LAYER).setData({
      type: 'FeatureCollection',
      features: featuresAr,
    });
  }

  const updateMarkers = async () => {
    if (!map?.getSource(CLUSTER_LAYER)) {
      return;
    }
    const queriedFeatures = map.querySourceFeatures(CLUSTER_LAYER);

    const providerFeatures = queriedFeatures?.filter(
      (feature) => !feature.properties?.cluster
    );
    const uniqueProviderFeatures = providerFeatures?.reduce((acc, feature) => {
      const id = feature.properties.providerId;
      if (!acc.find((f) => f.properties.providerId === id)) {
        acc.push(feature);
      }
      return acc;
    }, []);
    const pinsGroupedByCoordDistance = groupMapPinsByCoordinatesDistanceMiles(
      uniqueProviderFeatures,
      groupDistanceThresholdValue
    );
    const groupedPinFeatures = getGroupedFeatures(pinsGroupedByCoordDistance);
    const singlePinFeatures = getFeaturesWithOnePin(pinsGroupedByCoordDistance);

    queriedFeatures?.forEach(async (feature) => {
      if (feature.properties?.cluster) {
        const clusterId = feature.properties.cluster_id;
        const clusterSource = map.getSource(CLUSTER_LAYER);
        const [count, clusterFeatures] = await getClusterCountAndFeatures(
          clusterSource,
          clusterId
        );

        // mapMarkers.find and mapMarkers.push must not contain await expression in between;
        // otherwise, async flow may skip updated values in mapMarkers.find
        if (mapMarkers.find((marker) => marker.id === clusterId)) {
          return;
        }

        const clusterPinsGroupedByCoordDistance =
          groupMapPinsByCoordinatesDistanceMiles(
            clusterFeatures,
            groupDistanceThresholdValue
          );

        const clusterGroupedPinFeatures = getGroupedFeatures(
          clusterPinsGroupedByCoordDistance
        );
        const clusterSinglePinFeatures = getFeaturesWithOnePin(
          clusterPinsGroupedByCoordDistance
        );
        const isClusterASingleGroup =
          clusterGroupedPinFeatures.length === 1 &&
          clusterSinglePinFeatures.length === 0;

        if (isClusterASingleGroup) {
          createGroupedPinMarker(
            clusterGroupedPinFeatures[0],
            setGroupedLocationDrawerOpen,
            setDrawerContent,
            map,
            mapMarkers
          );
          return;
        }
        const coordinates = feature.geometry.coordinates;
        const el = clusterPinElement(count, map, coordinates, clusterId);
        const newMarker = new mapboxgl.Marker(el)
          .setLngLat(coordinates)
          .addTo(map);
        mapMarkers.push({
          id: clusterId,
          marker: newMarker,
        });
      }
    });

    singlePinFeatures?.forEach((feature) => {
      const { description } = feature.properties;
      const practitioner = JSON.parse(description);
      const markerId = practitioner.providerId ?? practitioner.locationId;

      if (mapMarkers.find((marker) => marker.id === markerId)) {
        return;
      }

      const el = createLocationMapPin(
        markerId,
        practitioner,
        location,
        map,
        highlightPinCallback,
        updatePin,
        enableMapEnhancements,
        mobileScreen,
        disabledPinAction,
        enableMapViewLargeResults
      );

      const newMarker = new mapboxgl.Marker(el)
        .setLngLat(feature._geometry.coordinates)
        .addTo(map);
      mapMarkers.push({
        id: markerId,
        marker: newMarker,
      });
    });

    groupedPinFeatures?.forEach((feature) => {
      createGroupedPinMarker(
        feature,
        setGroupedLocationDrawerOpen,
        setDrawerContent,
        map,
        mapMarkers
      );
    });
  };

  await updateMarkers();

  map.on('dragend', async () => {
    await setTimeout(async () => {
      removeAllMarkers();
      await updateMarkers();
    }, 300);
  });

  map.on('zoomend', async () => {
    await setTimeout(async () => {
      removeAllMarkers();
      await updateMarkers();
    }, 300);
  });

  if (featuresAr?.length > 0) {
    await setTimeout(async () => {
      removeAllMarkers();
      await updateMarkers();
    }, 300);
  }
};

export const updateIconSize = (
  isHighlighting,
  marker,
  enableMapEnhancements
) => {
  if (marker) {
    const img = marker.querySelector("[class^='map-icon']");
    if (img) {
      const iconWidth = isHighlighting ? '32.67px' : '23.33px';
      const iconHeight = isHighlighting ? '46.67px' : '33.33px';
      marker.style.width = iconWidth;
      marker.style.height = iconHeight;
      if (enableMapEnhancements) {
        img.className = isHighlighting ? 'map-icon-selected' : 'map-icon';
      }
    } else {
      marker.style.backgroundColor = isHighlighting
        ? 'var(--abyss-psx-parcel-colors-info1)'
        : 'var(--abyss-psx-parcel-colors-primary1)';
      marker.style.height = isHighlighting ? '20px' : '16px';
      marker.style.width = isHighlighting ? '20px' : '16px';
    }
  }
};

export const setResultIcon = (
  map,
  targetId,
  isHighlighting,
  highlightPinCallback,
  providerId
) => {
  const iconSize = isHighlighting ? 2 : 1;
  const loaded = map?.isStyleLoaded();
  const layer = map?.getLayer(RESULT_LAYER) ? RESULT_LAYER : CLUSTER_LAYER;
  if (loaded && map?.getLayer(layer))
    map?.setLayoutProperty(layer, 'icon-size', [
      'match',
      ['id'],
      String(targetId),
      iconSize,
      1,
    ]);
  if (map) {
    highlightPinCallback({ providerId, from: 'mapView' });
  }
};

export const clearRoute = (
  map,
  selectPinCallback?,
  highlightPinId?,
  highlightPinCallback?
) => {
  if (map?.getSource(ROUTE_LAYER)) {
    map.removeLayer(ROUTE_LAYER);
    map.removeSource(ROUTE_LAYER);
  }

  if (highlightPinCallback) {
    setResultIcon(map, highlightPinId, false, highlightPinCallback, null);
  }

  if (selectPinCallback) {
    selectPinCallback([null, null]);
  }
};

export const loadRoute = (
  map,
  routes,
  choosenRoute,
  userLng,
  userLat,
  endLng,
  endLat,
  mediumScreen = false
) => {
  clearRoute(map);
  const data = routes?.[choosenRoute];
  const route = data?.geometry?.coordinates;
  const geojson = {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'LineString',
      coordinates: route,
    },
  };

  if (map.getSource(ROUTE_LAYER)) {
    map.getSource(ROUTE_LAYER).setData(geojson);
  } else {
    map?.addLayer({
      id: ROUTE_LAYER,
      type: 'line',
      source: {
        type: 'geojson',
        data: geojson,
      },
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': '#3887be',
        'line-width': 5,
        'line-opacity': 0.75,
      },
    });
  }

  map.fitBounds(
    [
      [userLng, userLat],
      [endLng, endLat],
    ],
    { padding: mediumScreen ? 50 : 120 }
  );
};

export const getRoute = async (
  map,
  userLng,
  userLat,
  endLng,
  endLat,
  mediumScreen = false,
  navType?,
  selectedRoute = 0,
  mapOnPremKey = '',
  mapKey = '',
  mapOnPremURL = 'https://map.ce.uhg.com',
  mapLocationUrl = ''
): Promise<void | Directions> => {
  if (!map) {
    return;
  }

  if (navType) {
    curNavType = navType;
  }

  const mapLocationBasePath = mapLocationUrl || config('API_GATEWAY_URL');

  const mapGeoCodingUrl = `${mapLocationBasePath}/location-search`;

  const endLangTyped = parseFloat(endLng)?.toFixed(7);
  const endLatTyped = parseFloat(endLat)?.toFixed(7);
  let mapBoxURl: string;
  const locale = getLanguage()?.code || 'en';
  if (Navigation.some((navType) => curNavType.includes(navType))) {
    mapBoxURl = `${mapOnPremURL}/directions/v5/mapbox/${curNavType}/${userLng},${userLat};${endLangTyped},${endLatTyped}?steps=true&geometries=geojson&language=${locale}&alternatives=true&access_token=${mapOnPremKey}`;
  } else {
    mapBoxURl = `${mapOnPremURL}/directions/v5/mapbox/${curNavType}/${userLng},${userLat};${endLangTyped},${endLatTyped}?steps=true&geometries=geojson&language=${locale}&alternatives=true&exclude=toll&access_token=${mapOnPremKey}`;
  }

  const routeQuery = await fetch(
    mapBoxURl,

    { method: 'GET' }
  );
  const userPlaceQuery = await fetch(
    `${mapGeoCodingUrl}/mapbox.places/${userLng},${userLat}.json?types=address&&access_token=${mapKey}`,
    { method: 'GET' }
  );
  const endPlaceQuery = await fetch(
    `${mapGeoCodingUrl}/mapbox.places/${endLng},${endLat}.json?types=address&&access_token=${mapKey}`,
    { method: 'GET' }
  );
  const routeJson = await routeQuery.json();
  const userPlaceJson = await userPlaceQuery.json();
  const endPlaceJson = await endPlaceQuery.json();
  const { routes } = routeJson;
  const choosenRoute = selectedRoute < routes?.length ? selectedRoute : 0;
  const directions: Directions = {
    userPlaceName: userPlaceJson?.features?.[0]?.place_name,
    endPlaceName: endPlaceJson?.features?.[0]?.place_name,
    routes: routes?.map((route) => ({
      steps: route?.legs?.[0].steps,
      duration: route?.duration,
      distance: route?.distance,
      geometry: route?.geometry,
    })),
  };

  loadRoute(
    map,
    routes,
    choosenRoute,
    userLng,
    userLat,
    endLng,
    endLat,
    mediumScreen
  );

  // eslint-disable-next-line consistent-return
  return directions;
};

const mapClick = (
  map,
  highlightPinCallback,
  setPopupContent,
  enableMapEnhancements
) => {
  map.on('click', () => {
    if (map.getSource(ROUTE_LAYER)) {
      return;
    }

    if (clickResultId !== null) {
      setResultIcon(map, clickResultId, false, highlightPinCallback, null);
      updateIconSize(false, selectPin(clickResultId), enableMapEnhancements);
      clickResultId = null;
      setPopupContent?.(null);
      highlightPinCallback({ providerId: '', from: 'mapView' });
    }
  });
};

export const removeAllMarkers = () => {
  mapMarkers.map((mapMarker) => {
    if (mapMarker?.marker?.remove != undefined) mapMarker?.marker?.remove();
  });
  mapMarkers = [];
};

export const loadMap = async (
  map: {
    isStyleLoaded: () => any;
    getSource: (arg0: string) => any;
    addControl: (arg0: any, arg1: string) => void;
    getCanvas: () => {
      (): any;
      new (): any;
      setAttribute: { (arg0: string, arg1: string): void; new (): any };
    };
  },
  highlightPinCallback: Dispatch<
    SetStateAction<{ providerId: string; from: string }>
  >,
  setPopupContent:
    | ((content: ProviderLocation | FacilityLocationsResponse | null) => void)
    | undefined,
  enableMapEnhancements: boolean
) => {
  await map?.isStyleLoaded();
  if (!map?.getSource(USER_LAYER)) {
    map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    map.getCanvas()?.setAttribute('role', 'application');
    map.getCanvas()?.setAttribute('aria-label', t('ACCESSIBILITY_LABELS.MAP'));
    mapClick(map, highlightPinCallback, setPopupContent, enableMapEnhancements);
  }
};

export const getProviderResults = (
  data: Provider[],
  enableMapPinRedesign: boolean
) => {
  const coordinatesRef = {};

  return data
    ?.filter((p) => p.longitude && p.latitude)
    .map((practitioner: Provider) => {
      let coordinates = [practitioner.longitude, practitioner.latitude];
      const key = coordinates.join(',');
      if (coordinatesRef[key] && !enableMapPinRedesign) {
        // @ts-expect-error Ignore this
        const longSplits = practitioner.longitude.split('.');
        const fifthLongDecimal = parseInt(longSplits[1][4]);
        let randomValue: number;
        // @ts-expect-error Ignore this
        const latSplits = practitioner.latitude.split('.');
        const fifthLatDecimal = parseInt(latSplits[1][4]);
        let randomLatValue: number;
        do {
          randomValue = Math.floor(Math.random() * 10);
        } while (randomValue === fifthLongDecimal);
        const newLongitude = parseFloat(
          longSplits[0] +
            '.' +
            longSplits[1].substring(0, 4) +
            randomValue +
            longSplits[1].substring(5)
        );
        do {
          randomLatValue = Math.floor(Math.random() * 10);
        } while (randomLatValue === fifthLatDecimal);
        const newLatitude = parseFloat(
          latSplits[0] +
            '.' +
            latSplits[1].substring(0, 4) +
            randomLatValue +
            latSplits[1].substring(5)
        );
        coordinates = [String(newLongitude), String(newLatitude)];
      }
      coordinatesRef[key] = true;
      return {
        type: 'Feature',
        id: practitioner.providerId,
        properties: {
          description: JSON.stringify(practitioner),
          providerId: practitioner.providerId,
          type: 'provider',
        },
        geometry: {
          type: 'Point',
          coordinates,
        },
      };
    });
};

const selectPin = (pinId) => document.querySelector(`#custom-marker-${pinId}`);

export const escapeRegExp = (string: string) =>
  defaultStringToEmpty(string).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const getMapViewPageSize = (
  enableMapViewLargeResults: boolean,
  mapViewPageSizeConfig: string
): number => {
  if (enableMapViewLargeResults) {
    return parseInt(mapViewPageSizeConfig, 10) || PAGE_SIZE_MAP_LARGE;
  }

  return PAGE_SIZE_MAP_DEFAULT;
};

export const getFeaturesArray = (
  enableMapPinRedesign: boolean,
  providerPins: Provider[],
  providerResults: Provider[],
  locationResults: ProviderLocation[] | FacilityLocationsResponse[]
): Feature[] => {
  const providerFeatures = getProviderResults(
    enableMapPinRedesign ? providerPins : providerResults,
    enableMapPinRedesign
  );
  const locationFeatures = locationResults.map(
    (location: ProviderLocation | FacilityLocationsResponse) => ({
      type: 'Feature',
      id: location.locationId,
      properties: {
        description: JSON.stringify(location),
        providerId: location.locationId,
        type: 'location',
      },
      geometry: {
        type: 'Point',
        coordinates: [location.longitude, location.latitude],
      },
    })
  );

  return [...providerFeatures, ...locationFeatures].filter(
    (featureItem) =>
      parseInt(featureItem.geometry.coordinates[0] || '') &&
      parseInt(featureItem.geometry.coordinates[1] || '')
  );
};

export const getUniqueCoordinates = (featuresAr, longitude, latitude) => {
  const coordinates = [
    ...featuresAr.map((item) => item.geometry.coordinates),
    [longitude, latitude],
  ];

  const uniqueCoordinates: any[] = [];

  coordinates?.forEach((item, index) => {
    const currentCoordinate = item;
    const addToLatitude = `00.0000${index}`;
    uniqueCoordinates?.forEach((uniqueCoordinatesItem) => {
      if (
        uniqueCoordinatesItem.every(
          (value, index) => value === currentCoordinate[index]
        )
      ) {
        currentCoordinate[1] = (
          parseFloat(currentCoordinate[1] || '00.00000') +
          parseFloat(addToLatitude)
        ).toString();
      }
    });
    uniqueCoordinates.push(currentCoordinate);
  });

  return uniqueCoordinates;
};

export const cosineDistanceBetweenPoints = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
) => {
  const R = 3958.756;
  const p1 = (lat1 * Math.PI) / 180;
  const p2 = (lat2 * Math.PI) / 180;
  const deltaP = p2 - p1;
  const deltaLon = lon2 - lon1;
  const deltaLambda = (deltaLon * Math.PI) / 180;
  const a =
    Math.sin(deltaP / 2) * Math.sin(deltaP / 2) +
    Math.cos(p1) *
      Math.cos(p2) *
      Math.sin(deltaLambda / 2) *
      Math.sin(deltaLambda / 2);
  return 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) * R;
};

export const setMapCoords = (
  map,
  mapLatitude: string | undefined,
  mapLongitude: string | undefined,
  mapSearchRadius: number | undefined,
  setMapLatitude,
  setMapLongitude,
  setMapSearchRadius,
  isZoomChanged: boolean
) => {
  const mapCenter: { lat; lng } = map.getCenter();

  if (
    mapCenter.lat.toString() !== mapLatitude ||
    mapCenter.lng.toString() !== mapLongitude
  ) {
    setMapLatitude(mapCenter.lat.toString());
    setMapLongitude(mapCenter.lng.toString());
  }

  const bounds = map.getBounds();
  const northLat = bounds.getNorth();
  const eastLng = bounds.getEast();

  const ewCoordDiff = Math.abs(mapCenter.lng - eastLng);
  const nsCoordDiff = northLat - mapCenter.lat;
  const useLngForEdge = ewCoordDiff < nsCoordDiff;

  const radiusLng = useLngForEdge ? eastLng : mapCenter.lng;
  const radiusLat = useLngForEdge ? mapCenter.lat : northLat;

  const cosineDistance = cosineDistanceBetweenPoints(
    radiusLat,
    radiusLng,
    mapCenter.lat,
    mapCenter.lng
  );

  const searchRadius = cosineDistance > 120 ? 120 : cosineDistance;

  if (isZoomChanged && searchRadius !== mapSearchRadius) {
    setMapSearchRadius(searchRadius);
  }
};

// This function is used to calculate the maximum width of a popup
// based on the container's width and the map's width.
export const maxPopupWidth = (container?: HTMLElement, defaultValue = 300) => {
  const SAFE_MARGIN = 30;
  const mapContainer = document.querySelector('.map-container');
  if (!container) {
    if (mapContainer)
      return Math.min(mapContainer.clientWidth - SAFE_MARGIN, defaultValue);

    return defaultValue;
  }

  if (!mapContainer) return container.offsetWidth;

  return Math.min(
    container.offsetWidth,
    mapContainer.clientWidth - SAFE_MARGIN
  );
};

// This function is used to calculate the maximum height of a popup
// based on the container's height and the map's height.
export const maxPopupHeight = (container?: HTMLElement, defaultValue = 250) => {
  const SAFE_MARGIN = 30;
  const mapContainer = document.querySelector('.map-container');
  if (!container) {
    if (mapContainer)
      return Math.min(mapContainer.clientHeight - SAFE_MARGIN, defaultValue);

    return defaultValue;
  }

  if (!mapContainer) return container.offsetHeight;

  return Math.min(
    container.offsetHeight,
    mapContainer.clientHeight - SAFE_MARGIN
  );
};

export class PsxMapGlPopup extends mapboxgl.Popup {
  _map;
  _pos;
  _container;
  _content;
  _lngLat;
  _trackPointer;

  constructor(options) {
    super(options);
  }

  setLngLat(lngLat) {
    return super.setLngLat(lngLat);
  }

  setHTML(html) {
    return super.setHTML(html);
  }

  adjustPopupBoundsToViewport() {
    const map = this._map;

    const container = this._container;

    if (!container) return;

    const dataCardContainer = this._container?.querySelector('.datacard-popup');
    if (!dataCardContainer) return;

    const transformString = container.style.transform;
    // transform: translate(0px, -100%) translate(250px, 0px);
    // translateValues = [0, -100%, 250, 0]
    const translateValuesArray = transformString.match(/-?\d+%|-?\d+px/g) || [];

    const translateValues = translateValuesArray.map((value) =>
      value.endsWith('px') ? parseInt(value) : value
    );

    const SAFE_MARGIN = 20;
    const mapContainer = map.getContainer();

    const mapWidth = mapContainer.offsetWidth - SAFE_MARGIN;
    const mapHeight = mapContainer.offsetHeight - SAFE_MARGIN;

    const nonProviderPopup = document.querySelector(
      '.non-provider-popup'
    ) as HTMLElement;
    let dataCardContainerWidth = Math.max(
      nonProviderPopup?.offsetWidth || dataCardContainer.offsetWidth,
      500
    );
    let dataCardContainerHeight = Math.max(
      nonProviderPopup?.offsetHeight || dataCardContainer.offsetHeight,
      280
    );
    const leftAnchor = (mapWidth - dataCardContainerWidth) / 2;
    const bottomAnchor = (mapHeight - dataCardContainerHeight) / 2;
    let isWidthChanged = false;
    let isHeightChanged = false;

    if (dataCardContainerWidth > mapWidth) {
      dataCardContainerWidth = mapWidth;
      translateValues[2] = 15;
      isWidthChanged = true;
      if (dataCardContainer.offsetWidth) {
        dataCardContainer.style.width = `${dataCardContainerWidth}px`;
        dataCardContainer.style.overflowX = 'auto';
      }
    } else {
      translateValues[2] = leftAnchor;
    }

    if (dataCardContainerHeight > mapHeight) {
      dataCardContainerHeight = mapHeight;
      translateValues[3] = 15;
      isHeightChanged = true;
      if (dataCardContainer.offsetHeight) {
        dataCardContainer.style.height = `${dataCardContainerHeight}px`;
        dataCardContainer.style.overflowY = 'auto';
      }
    } else {
      translateValues[3] = bottomAnchor;
    }

    requestAnimationFrame(() => {
      if (this._container) {
        this._container.style.transform = `translate(0,0) translate(${translateValues[2]}px,${translateValues[3]}px)`;
        const dc = this._container.querySelector('.datacard-popup');
        if (dc.offsetWidth) {
          if (isWidthChanged) {
            dc.style.width = `${dataCardContainerWidth}px`;
          }
          if (isHeightChanged) {
            dc.style.height = `${dataCardContainerHeight}px`;
            dc.style.overflowY = 'auto';
          }
        }
      }
    });
  }

  _update(cursor) {
    const map = this._map;
    if (!map) {
      return;
    }
    super._update(cursor);

    const mapContainer = map.getContainer();
    const container = this._container;
    const dataCardContainer = this._container?.querySelector('.datacard-popup');
    const centerRenderPopup = document.querySelector(
      '.map-viewport-constrained-popup'
    ) as HTMLElement;

    if (centerRenderPopup) {
      this.adjustPopupBoundsToViewport();
      return;
    }
    const mapClientRect = mapContainer.getBoundingClientRect();
    const containerClientRect = container.getBoundingClientRect();
    if (container?.offsetHeight) {
      const isHeightOverflow = container.offsetHeight > mapClientRect.height;
      const isCutoffTop = containerClientRect.top < mapClientRect.top;
      const isCutoffBottom = containerClientRect.bottom > mapClientRect.bottom;
      const isCutoffRight =
        this._pos?.x > mapClientRect.right - mapClientRect.left;
      const isCutoffLeft = this._pos?.x - container.offsetWidth - 5 < 0;

      let isUpdatedTranslate = false;
      const transformString = container.style.transform;
      // transform: translate(0px, -100%) translate(250px, 0px);
      // translateValues = [0, -100%, 250, 0]
      const translateValuesArray =
        transformString.match(/-?\d+%|-?\d+px/g) || [];

      const translateValues = translateValuesArray.map((value) =>
        value.endsWith('px') ? parseInt(value) : value
      );

      if (dataCardContainer && isHeightOverflow) {
        dataCardContainer.style.height = `${mapClientRect.height - 25}px`;
        dataCardContainer.style.overflowY = 'auto';
      }

      if (isCutoffBottom) {
        translateValues[3] = mapClientRect.height;
        translateValues[0] = '10%';
        translateValues[1] = '-100%';
        isUpdatedTranslate = true;
      } else if (isCutoffTop) {
        translateValues[3] = 0;
        isUpdatedTranslate = true;
      }

      if (isCutoffRight) {
        translateValues[2] =
          mapClientRect.width - containerClientRect.width - 15;
        translateValues[0] = '10%';
        isUpdatedTranslate = true;
      }
      if (isCutoffLeft) {
        translateValues[2] = this._pos.x + 12 < 0 ? 10 : this._pos.x + 10;
        translateValues[0] = '10%';
        isUpdatedTranslate = true;
      }

      if (isUpdatedTranslate) {
        if (!isCutoffRight && !isCutoffLeft) {
          translateValues[0] = '-110%';
        }
        if (typeof translateValues[1] === 'number')
          translateValues[1] = translateValues[1] + 'px';

        requestAnimationFrame(() => {
          container.style.transform = `translate(${translateValues[0]}, ${translateValues[1]}) translate(${translateValues[2]}px,${translateValues[3]}px)`;
        });
      }
    }
  }
}

export const getSidePanelArrowIcon = (closeSidePanel: boolean) =>
  closeSidePanel ? 'keyboard_arrow_right' : 'keyboard_arrow_left';

export const getDataCardMinHeight = (mobileScreen: boolean) =>
  mobileScreen ? '180px' : '196px';

export const getFinalProviderResults = (selectedItems: any[], results: any[]) =>
  selectedItems.length > 0 ? selectedItems : results;

export const mapViewUpdatePin = (
  practitioner,
  flyToPin,
  popupContent,
  setPopupContent,
  map
) => {
  const popup = document.getElementsByClassName('mapboxgl-popup');
  if (
    practitioner &&
    practitioner.providerId === popupContent?.providerId &&
    practitioner.locationId === popupContent?.locationId &&
    popup.length
  ) {
    return;
  }
  if (popup.length) {
    for (let i = 0; i < popup.length; i++) {
      popup[i].remove();
    }
  }
  setPopupContent({ ...practitioner });

  if (!practitioner) {
    return;
  }

  if (practitioner?.address?.line?.[0]) {
    new PsxMapGlPopup({
      closeButton: false,
    })
      .setLngLat([+practitioner.longitude, +practitioner.latitude])
      .setHTML(
        `<div class="datacard-popup" style="border:none;visibility:hidden;"></div>`
      )
      .addTo(map.current);
  } else {
    new PsxMapGlPopup({
      closeButton: false,
    })
      .setLngLat(map.current.getCenter())
      .setHTML(
        `<div class="datacard-popup map-viewport-constrained-popup" style="border:none;visibility:hidden;"></div>`
      )
      .addTo(map.current);
  }

  document.querySelectorAll('.mapboxgl-popup-tip')?.forEach((el) => {
    (el as HTMLElement).style.display = 'none';
  });

  if (flyToPin) {
    map.current?.flyTo({
      center: [+practitioner.longitude, +practitioner.latitude],
      zoom: Constants.DEVICE_LOCATION.ZOOM_HOVER,
    });
  }
};

export const getProviderForPinDetails = (
  providerResult,
  providerType,
  latitude,
  longitude
) => {
  let provider;

  switch (providerType) {
    case ProviderType.ORGANIZATION:
      provider = providerResult?.data?.facilities?.[0];
      break;
    case ProviderType.MEDICAL_GROUP:
      provider = providerResult?.data?.medicalGroup?.[0];
      break;
    default:
      provider = providerResult?.data?.providers?.[0];
  }

  if (provider && !provider.distance) {
    provider.distance = cosineDistanceBetweenPoints(
      provider.latitude,
      provider.longitude,
      latitude,
      longitude
    );
  }

  return provider;
};

export const groupMapPinsByCoordinatesDistanceMiles = (
  features: Feature[],
  distanceThreshold: number = GROUP_DISTANCE_THRESHOLD_MILES_DEFAULT
): Record<string, Feature[]> => {
  const grouped: Record<string, Feature[]> = {};

  features?.forEach((feature) => {
    const coords = feature.geometry.coordinates;
    const key = `${coords[0]},${coords[1]}`;

    if (!grouped[key]) {
      grouped[key] = [];
    }

    for (const existingKey in grouped) {
      const existingCoords = existingKey.split(',');

      const distance = cosineDistanceBetweenPoints(
        parseFloat(existingCoords[1] ?? '0'),
        parseFloat(existingCoords[0] ?? '0'),
        parseFloat(coords[1] ?? '0'),
        parseFloat(coords[0] ?? '0')
      );

      if (distance <= distanceThreshold) {
        grouped[existingKey].push(feature);
        return;
      }
    }
  });

  return grouped;
};

export const groupMapPinsByCoordinates = (
  features: Feature[]
): Record<string, Feature[]> =>
  features?.reduce<Record<string, Feature[]>>((acc, feature) => {
    const key = feature.geometry.coordinates.join(',');

    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(feature);
    return acc;
  }, {});

export const getGroupedFeatures = (
  groupedMapPinsByCoords: Record<string, Feature[]>
) =>
  Object.entries(groupedMapPinsByCoords || {})
    .filter(([, features]) => features.length > 1)
    .map(([key, pins]) => ({
      ...pins[0],
      properties: pins,
      id: key,
    }));

export const getFeaturesWithOnePin = (
  groupedMapPinsByCoords: Record<string, Feature[]> | undefined
) =>
  Object.entries(groupedMapPinsByCoords || {})
    .filter(([, features]) => features.length === 1)
    .map(([, feature]) => ({
      ...feature[0],
    }));

export const stringToIntegerOrDefault = (
  value: string,
  defaultValue: number
) => {
  const parsedValue = parseInt(value);
  return isNaN(parsedValue) ? defaultValue : parsedValue;
};

export const stringToFloatOrDefault = (value: string, defaultValue: number) => {
  const parsedValue = parseFloat(value);
  return isNaN(parsedValue) ? defaultValue : parsedValue;
};
